import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Filter, MapPin, Calendar } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PackageCard from "../components/PackageCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    filterPackages();
  }, [searchTerm, categoryFilter, packages]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages?audience=website`);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterPackages = () => {
    let filtered = [...packages];
    
    if (searchTerm) {
      filtered = filtered.filter(pkg => 
        pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.destination.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(pkg => pkg.category === categoryFilter);
    }
    
    setFilteredPackages(filtered);
  };

  return (
    <div className="min-h-screen" data-testid="packages-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-primary-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-secondary-500 font-accent text-xl">Explore</span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-2 mb-6">
              Our Travel Packages
            </h1>
            <p className="text-white/80 max-w-2xl mx-auto">
              Discover handcrafted travel experiences designed for your clients. From tropical escapes to mountain adventures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input 
                placeholder="Search destinations..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-muted-foreground" />
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40" data-testid="category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <span className="text-sm text-muted-foreground">
                {filteredPackages.length} packages found
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700" />
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No packages found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { setSearchTerm(""); setCategoryFilter("all"); }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <PackageCard pkg={pkg} onViewDetails={setSelectedPackage} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-muted-foreground mb-6">
            We create custom packages tailored to your client's needs. Contact us for a personalized quote.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-primary-700 hover:bg-primary-900">Request Custom Package</Button>
            </Link>
            <Link to="/agent-register">
              <Button variant="outline">Become an Agent</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Package Detail Dialog */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPackage && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{selectedPackage.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <img 
                  src={selectedPackage.image_url} 
                  alt={selectedPackage.title}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {selectedPackage.destination}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {selectedPackage.duration}</span>
                </div>
                <p className="text-foreground">{selectedPackage.description}</p>
                
                <div>
                  <h4 className="font-semibold mb-2">Highlights</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {selectedPackage.highlights?.map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-1.5 h-1.5 bg-primary-700 rounded-full" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2 text-green-700">Inclusions</h4>
                    <ul className="space-y-1">
                      {selectedPackage.inclusions?.map((inc, i) => (
                        <li key={i} className="text-sm text-muted-foreground">✓ {inc}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2 text-red-700">Exclusions</h4>
                    <ul className="space-y-1">
                      {selectedPackage.exclusions?.map((exc, i) => (
                        <li key={i} className="text-sm text-muted-foreground">✗ {exc}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <span className="text-sm text-muted-foreground">Starting from</span>
                    <p className="text-2xl font-bold text-primary-700">₹{selectedPackage.price?.toLocaleString()}</p>
                    <span className="text-xs text-muted-foreground">per person</span>
                  </div>
                  <Link to="/agent-register">
                    <Button className="bg-primary-700 hover:bg-primary-900">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
